// uiScale — the app's font and icon sizing, as a persisted store.
//
// The docked shell's chrome was authored at design sizes (9–11.5px mono), which
// read small on a large monitor. Rather than re-author every px, the FONT size
// rides a zoom on the app root (Chromium and Firefox 126+ both honor CSS zoom;
// layout reflows correctly, unlike transform:scale), and the ICON size rides a
// CSS variable (--aos-icon-scale) consumed by the glyph-bearing controls (tool
// rail, menubar chip, dropdown glyphs).
//
// Steps are named, not free numbers, so the Settings UI is three buttons per
// row and a stored value is always one we've eyeballed. Default is MEDIUM for
// both — one step up from the design's original 1.0, per review feedback that
// the chrome reads too small.

const LS_KEY = 'aos-ui-scale-v1'

export const FONT_STEPS = Object.freeze({ small: 1, medium: 1.1, large: 1.25 })
// Icon steps are centred on PlayCanvas parity: the tool rail is authored at its
// 40px button / 24px glyph, so MEDIUM (1.0) reproduces that editor exactly and
// the other two steps are honest deviations from it. (Font steps are not
// centred the same way — 'small' there is the design's own size.)
export const ICON_STEPS = Object.freeze({ small: 0.85, medium: 1, large: 1.2 })

const DEFAULTS = Object.freeze({ font: 'medium', icon: 'medium' })

export function sanitize(candidate) {
  const c = candidate && typeof candidate === 'object' ? candidate : {}
  return {
    font: c.font in FONT_STEPS ? c.font : DEFAULTS.font,
    icon: c.icon in ICON_STEPS ? c.icon : DEFAULTS.icon,
  }
}

function load() {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null
    return sanitize(raw ? JSON.parse(raw) : DEFAULTS)
  } catch {
    return { ...DEFAULTS }
  }
}

let state = load()
const listeners = new Set()

/** Write the scales onto the document. Idempotent; called at boot and on change. */
function apply() {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const font = FONT_STEPS[state.font]
  root.style.zoom = String(font)
  root.style.setProperty('--aos-icon-scale', String(ICON_STEPS[state.icon]))
  // Published so subtrees that cannot live under a CSS zoom can undo it —
  // see the .react-flow rule in styles/index.css. Kept here (rather than
  // recomputed at each use site) so the zoom and its inverse can never drift.
  root.style.setProperty('--aos-ui-zoom', String(font))
  root.style.setProperty('--aos-ui-zoom-inv', String(1 / font))
}

function persist() {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_KEY, JSON.stringify(state))
  } catch { /* private mode */ }
}

export const uiScale = {
  getState() { return state },

  setState(patch = {}) {
    state = sanitize({ ...state, ...patch })
    persist()
    apply()
    for (const fn of listeners) fn(state)
  },

  /** Boot hook — applies the persisted scales before first paint matters. */
  apply,

  subscribe(fn) {
    listeners.add(fn)
    fn(state)
    return () => listeners.delete(fn)
  },

  /** Test seam. */
  _reset() {
    state = { ...DEFAULTS }
    listeners.clear()
  },
}

export default uiScale
